<template>
    <li
        class="ds-menu-option cursor-pointer"
        :class="{ 'pointer-events-none opacity-50': disabled }"
        role="menuitem"
        tabindex="0"
        @click="handleClick"
        @keydown.enter="handleClick"
        @keydown.space.prevent="handleClick"
    >
        <component
            :is="href ? 'a' : 'div'"
            class="flex items-center justify-between gap-3 rounded-lg p-3 text-content-lg no-underline"
            :class="variantClass"
            :download
            :href
            :target
        >
            <div class="flex items-center justify-start gap-3">
                <template v-if="$slots.leading || leading">
                    <slot name="leading">
                        <DsIcon
                            v-if="typeof leading === 'string'"
                            :class="variantClass"
                            :icon="leading"
                            size="md"
                        />
                        <component
                            v-else-if="leading"
                            :is="leading"
                        />
                    </slot>
                </template>

                <span :class="variantClass">
                    <slot />
                </span>
            </div>

            <template v-if="$slots.trailing || trailing">
                <slot name="trailing">
                    <DsIcon
                        v-if="typeof trailing === 'string'"
                        :class="variantClass"
                        :icon="trailing"
                        size="md"
                    />
                    <component
                        v-else-if="trailing"
                        :is="trailing"
                    />
                </slot>
            </template>
        </component>
    </li>
</template>

<script setup lang="ts">
import DsIcon from '@/components/design-system/DsIcon.vue';
import { computed, inject } from 'vue';
import type { DsMenuOptionProps } from './menu.types';

const props = withDefaults(defineProps<DsMenuOptionProps>(), {
    variant: 'primary',
    disabled: false,
});

const emit = defineEmits<{
    click: [];
}>();

// Get the close function from DsMenu
const closeMenu = inject<(() => void) | null>('closeMenu', null);

const variantClass = computed(() => {
    return props.variant === 'destructive' ? 'destructive' : 'primary';
});

const handleClick = () => {
    if (props.disabled) return;
    emit('click');
    closeMenu?.();
};
</script>

<style scoped lang="scss">
.ds-menu-option {
    @apply list-none;
    > * {
        &:hover {
            @apply bg-semantic-secondary-hover;
        }

        &:active {
            @apply bg-semantic-secondary-pressed;
        }
    }
}

.destructive {
    @apply text-ds-red-600;
}

.primary {
    @apply text-content-primary;
}
</style>
